<template>

    <!-- Scan a receipt: pre-fills the add-expense form instead of opening it blank -->
    <input
        ref="receiptInput"
        type="file"
        accept="image/*"
        capture="environment"
        class="hidden"
        @change="onReceiptSelected"
    />

    <!-- Speed-dial FAB: hover opens it on desktop, tap toggles it on mobile (see isFabMenuOpen) -->
    <div
        class="fab-menu fixed z-40 bottom-4 right-4 sm:bottom-6 sm:right-8"
        :class="{ 'fab-menu-open': isFabMenuOpen }"
        @mouseenter="openFabMenuOnHover"
        @mouseleave="closeFabMenuOnHover"
    >
        <div class="fab-menu-actions">
            <button
                type="button"
                class="fab-action mt-12 bg-gray-800 ring-2 ring-inset ring-cyan-600 hover:bg-gray-900"
                :disabled="scanning"
                :aria-label="$t('receipt.scan')"
                @click="triggerReceiptScan"
            >
                <span class="material-symbols-outlined">photo_camera</span>
            </button>

            <button
                type="button"
                class="fab-action mb-12 bg-gray-800 ring-2 ring-inset ring-cyan-600 hover:bg-gray-900"
                :aria-label="$t('modals.expense.add')"
                @click="triggerManualAdd"
            >
                <span class="material-symbols-outlined">edit_note</span>
            </button>
        </div>

        <button
            type="button"
            class="fab-main bg-red-500 hover:bg-red-700"
            :aria-label="$t('modals.expense.add')"
            :aria-expanded="isFabMenuOpen"
            @click="toggleFabMenu"
        >
            <span class="material-symbols-outlined">add</span>
        </button>
    </div>

</template>

<script>
import { ref, watch, onBeforeUnmount } from 'vue';
import { useReceiptScan } from '@/composables/useReceiptScan';

// Speed-dial FAB shared by every view with an "add expense" flow: a single "+" button that
// reveals a manual-add and a scan-receipt action around it (hover on desktop, tap on mobile),
// instead of two separate always-visible buttons. Handles the receipt scan itself (file picker +
// OCR) and just hands the caller the result - callers only care about "add expense, optionally
// pre-filled", not how the photo got read.
export default {
    emits: ['add', 'scanned', 'update:scanning'],
    setup(props, { emit }) {
        const { scanning, scanReceipt } = useReceiptScan();
        watch(scanning, (value) => emit('update:scanning', value));

        const receiptInput = ref(null);

        // Hover reveals the two actions on devices that actually have hover (a real pointer),
        // tapping the main button toggles it on touch devices instead - matches how e.g.
        // Pinterest's own FAB behaves, just with 2 actions instead of a full radial menu.
        const isFabMenuOpen = ref(false);
        const supportsHover = typeof window !== 'undefined' && window.matchMedia('(hover: hover)').matches;

        // The main button and the revealed actions don't touch (there's a visual gap between
        // them), so moving the mouse from one to the other briefly crosses dead space that
        // belongs to neither - an immediate close-on-mouseleave would close the menu right as
        // the pointer is in transit, before it ever reaches a satellite button. A short grace
        // period (cancelled by openFabMenuOnHover firing again, which it does the instant the
        // pointer lands back on the main button or an action) fixes that without needing to
        // fill in the gap visually.
        let closeTimer = null;

        const openFabMenuOnHover = () => {
            if (supportsHover) {
                clearTimeout(closeTimer);
                isFabMenuOpen.value = true;
            }
        };
        const closeFabMenuOnHover = () => {
            if (supportsHover) {
                clearTimeout(closeTimer);
                closeTimer = setTimeout(() => {
                    isFabMenuOpen.value = false;
                }, 300);
            }
        };
        onBeforeUnmount(() => clearTimeout(closeTimer));

        const toggleFabMenu = () => {
            if (!supportsHover) {
                isFabMenuOpen.value = !isFabMenuOpen.value;
            }
        };

        const triggerManualAdd = () => {
            isFabMenuOpen.value = false;
            emit('add');
        };

        const triggerReceiptScan = () => {
            isFabMenuOpen.value = false;
            receiptInput.value.click();
        };

        // Triggered once a photo is actually picked (opens the camera on mobile via `capture`)
        const onReceiptSelected = async (event) => {
            const file = event.target.files[0];
            event.target.value = ''; // so picking the same file again still fires 'change'
            if (!file) {
                return;
            }

            emit('scanned', await scanReceipt(file));
        };

        return {
            scanning,
            receiptInput,
            isFabMenuOpen,
            openFabMenuOnHover,
            closeFabMenuOnHover,
            toggleFabMenu,
            triggerManualAdd,
            triggerReceiptScan,
            onReceiptSelected,
        };
    }
}
</script>

<style>
/* Speed-dial FAB: .fab-menu-open (set on hover on pointer devices, on click/tap otherwise - see
   openFabMenuOnHover/toggleFabMenu) reveals the two actions stacked above the main button. */
.fab-main {
    @apply relative z-10 w-16 h-16 rounded-full drop-shadow-lg flex justify-center
           items-center text-on-accent text-4xl duration-300;
}

/* pointer-events-none here (not just on the buttons below) matters: this box is absolutely
   positioned, so it doesn't add to .fab-menu's own layout size, but it's still a DOM descendant
   of .fab-menu - hovering this reserved dead space (before the menu ever opens) would otherwise
   still fire .fab-menu's mouseenter and pop the actions open, before the pointer ever reaches
   the visible + button. */
.fab-menu-actions {
    @apply absolute bottom-5 right-5 flex flex-row items-center gap-0 pointer-events-none;
}

.fab-menu-open .fab-menu-actions {
    @apply pointer-events-auto;
}

.fab-action {
    @apply w-12 h-12 rounded-full drop-shadow-lg flex justify-center items-center
           text-white text-xl duration-200
           opacity-0 scale-75 translate-y-2;
}

.fab-menu-open .fab-action {
    @apply opacity-100 scale-100 translate-y-0;
}
</style>
